import React, { useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import _get from 'lodash/get';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import Avatar from '../../components/avatar/avatar.component';
import Nametag from '../../components/cosmetics/nametag.component';
import { invalidateCosmetic } from '../../components/cosmetics/cosmetics-store';
import { routePaths } from '../../routes/routes-path';
import {
  getInventory,
  equipStoreItem,
  InventoryItem,
} from '../../api/payment/store.api';

const CARD =
  'flex w-full flex-col gap-3 rounded-[20px] bg-white dark:bg-dark2 p-5 shadow-[4px_4px_44px_rgba(0,0,0,0.06)] dark:shadow-none';

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString('vi-VN') : null;

const InventoryPage: React.FC = () => {
  const profile = useSelector((state: RootState) => state.features.auth.profile);
  const myUserId = _get(profile, 'userId', '') as string;

  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const inv = await getInventory();
      setInventory(inv || []);
    } catch {
      /* rỗng */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const nametags = useMemo(
    () => inventory.filter((i) => i.type === 'Nametag'),
    [inventory]
  );
  const effects = useMemo(
    () => inventory.filter((i) => i.type === 'AvatarEffect'),
    [inventory]
  );
  const consumables = useMemo(
    () => inventory.filter((i) => i.type !== 'Nametag' && i.type !== 'AvatarEffect'),
    [inventory]
  );

  const handleEquip = async (inv: InventoryItem) => {
    if (busyId) return;
    setBusyId(inv.id);
    try {
      const res = await equipStoreItem(inv.id, !inv.isEquipped);
      if (res.success) {
        const list = await getInventory();
        setInventory(list || []);
        invalidateCosmetic(myUserId); // cập nhật ngay preview + hiển thị khắp app
      } else {
        toast.error(res.message || 'Không thực hiện được');
      }
    } catch {
      toast.error('Có lỗi khi cập nhật vật phẩm');
    } finally {
      setBusyId(null);
    }
  };

  const CosmeticCard: React.FC<{ inv: InventoryItem }> = ({ inv }) => {
    const expiry = fmtDate(inv.expiresAt);
    return (
      <div className={CARD}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[16px] font-semibold text-[#19052f]">{inv.name}</span>
          {inv.isEquipped && (
            <span className="rounded-full border border-[#1E7D34] bg-[#ECFDF3] px-2 py-0.5 text-[12px] font-medium text-[#1E7D34]">
              Đang dùng
            </span>
          )}
        </div>
        <div className="text-[13px] text-[#657384]">
          {expiry ? `Hết hạn: ${expiry}` : 'Vĩnh viễn'}
        </div>
        <button
          onClick={() => handleEquip(inv)}
          disabled={busyId === inv.id}
          className={`mt-1 w-full rounded-full py-2 text-[15px] font-medium transition disabled:opacity-60 ${
            inv.isEquipped
              ? 'border border-[#066bff] bg-white dark:bg-slate-800 text-[#066bff] dark:text-blue-300'
              : 'bg-[#066bff] text-white hover:brightness-105'
          }`}
        >
          {busyId === inv.id ? 'Đang xử lý…' : inv.isEquipped ? 'Tháo ra' : 'Gắn'}
        </button>
      </div>
    );
  };

  const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
  }) => (
    <section className="flex w-full flex-col gap-4">
      <h2 className="px-1 text-[20px] font-semibold tracking-tight text-[#19052f]">
        {title}
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {children}
      </div>
    </section>
  );

  const empty = !loading && inventory.length === 0;

  return (
    <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-8 px-6 py-6 pb-16">
      <div className="flex flex-col gap-1">
        <h1 className="text-[28px] font-semibold tracking-tight text-[#19052f] md:text-[32px]">
          Kho đồ của bạn
        </h1>
        <p className="text-[15px] text-[#657384]">
          Quản lý và gắn vật phẩm đã sở hữu. Nametag đổi màu tên, hiệu ứng viền quanh avatar.
        </p>
      </div>

      {/* Preview trực tiếp cosmetic đang gắn của chính mình */}
      <div className="flex items-center gap-4 rounded-[20px] bg-white dark:bg-dark2 p-5 shadow-[4px_4px_44px_rgba(0,0,0,0.05)] dark:shadow-none">
        <Avatar
          avatar={_get(profile, 'avatar', '') as string}
          label={_get(profile, 'fullName', _get(profile, 'username', '')) as string}
          size="md"
          userId={myUserId}
        />
        <div className="flex flex-col">
          <span className="text-[12px] text-[#657384]">Xem trước</span>
          <Nametag userId={myUserId} banner compact>
            <span className="text-[18px] font-semibold text-[#19052f]">
              {_get(profile, 'fullName', _get(profile, 'username', 'Bạn')) as string}
            </span>
          </Nametag>
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <Spin />
        </div>
      ) : empty ? (
        <div className="flex flex-col items-center gap-4 rounded-[20px] bg-white dark:bg-dark2 p-10 text-center shadow-[4px_4px_44px_rgba(0,0,0,0.05)] dark:shadow-none">
          <p className="text-[16px] text-[#657384]">Kho đồ đang trống.</p>
          <Link
            to={routePaths.marketplace}
            className="rounded-full bg-[#066bff] px-6 py-2 text-[15px] font-medium text-white hover:brightness-105"
          >
            Đến Chợ mua vật phẩm
          </Link>
        </div>
      ) : (
        <div className="flex w-full flex-col gap-10">
          {nametags.length > 0 && (
            <Section title="Nametag">
              {nametags.map((i) => (
                <CosmeticCard key={i.id} inv={i} />
              ))}
            </Section>
          )}
          {effects.length > 0 && (
            <Section title="Hiệu ứng avatar">
              {effects.map((i) => (
                <CosmeticCard key={i.id} inv={i} />
              ))}
            </Section>
          )}
          {consumables.length > 0 && (
            <Section title="Vật phẩm khác">
              {consumables.map((i) => (
                <div key={i.id} className={CARD}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[16px] font-semibold text-[#19052f]">
                      {i.name}
                    </span>
                    <span className="rounded-full bg-[#EAEFFA] px-2.5 py-0.5 text-[13px] font-semibold text-[#066bff]">
                      x{i.quantity}
                    </span>
                  </div>
                  <div className="text-[13px] text-[#657384]">
                    Dùng khi tính năng liên quan ra mắt (Đấu trường / Clan / Dating).
                  </div>
                </div>
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
